import { useRef } from "react";

import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";

import { Bar } from "react-chartjs-2";

import ChartToolbar from "./ChartToolbar";


// Register the Chart.js components required for this bar chart.
ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);


function StationTimeChart({
  data = [],
  chargerType = "Both"
}) {
  const chartRef = useRef(null);


  const AC_COLOUR = "#2e7d32";
  const DC_COLOUR = "#f9a825";


  // Determine which charger types should be displayed.
  const showAC = chargerType === "Both" || chargerType === "AC";
  const showDC = chargerType === "Both" || chargerType === "DC";


  // Keep stations matching the current charger filter that contain
  // a valid average charging time.
  const stationData = Array.isArray(data)
    ? data.filter((row) => {
        const averageTime =
          Number(row.averageTimeMinutes);

        const validType =
          (row.chargerType === "AC" && showAC) ||
          (row.chargerType === "DC" && showDC);

        return (
          validType &&
          !Number.isNaN(averageTime)
        );
      })
    : [];


  // Sort stations alphabetically.
  stationData.sort((a, b) => {
    const aName =
      a.locationName ||
      a.chargingStationId ||
      "Unknown Location";

    const bName =
      b.locationName ||
      b.chargingStationId ||
      "Unknown Location";

    return aName.localeCompare(bName);
  });


  const labels = stationData.map((row) => {
    const name =
      row.locationName ||
      row.chargingStationId ||
      "Unknown Location";

    return `${name} (${row.chargerType})`;
  });


  const chartData = {
    labels,
    datasets: [
      {
        label: "Average Charging Time",
        data: stationData.map((row) =>
          Number(row.averageTimeMinutes)
        ),
        backgroundColor: stationData.map((row) =>
          row.chargerType === "DC"
            ? DC_COLOUR
            : AC_COLOUR
        ),
        borderColor: stationData.map((row) =>
          row.chargerType === "DC"
            ? DC_COLOUR
            : AC_COLOUR
        ),
        borderWidth: 1,
        barPercentage: 0.9,
        categoryPercentage: 0.85,
        maxBarThickness: 48
      }
    ]
  };


  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    indexAxis: "y",
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Average Charging Time (minutes)"
        }
      },
      y: {
        ticks: {
          autoSkip: false
        },
        title: {
          display: true,
          text: "Charging Station"
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (!items.length) {
              return "";
            }

            return labels[items[0].dataIndex];
          },

          label: (context) => {
            const row =
              stationData[context.dataIndex];

            return [
              `Average time: ${Number(
                row.averageTimeMinutes
              ).toFixed(1)} minutes`,
              `Sessions: ${Number(
                row.sessionCount
              ).toLocaleString()}`
            ];
          }
        }
      }
    }
  };


  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <div className="chart-card-title">
          <h2>
            Average Charging Time by Station
          </h2>

          <p>
            Compare average charging-session duration across
            individual charging stations.
          </p>
        </div>


        <ChartToolbar
          chartRef={chartRef}
          title="Average Charging Time by Station"
          enableZoom={false}
        />
      </div>


      <div className="chart-canvas-container">
        {stationData.length > 0 ? (
          <Bar
            ref={chartRef}
            data={chartData}
            options={options}
          />
        ) : (
          <div className="chart-empty-state">
            No station charging-time data available for the selected filters.
          </div>
        )}
      </div>
    </article>
  );
}


export default StationTimeChart;